import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Radio,
  Row,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ReloadOutlined,
  RadarChartOutlined,
  HeatMapOutlined,
  ArrowRightOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { authFetch } from '../../utils/api';
import {
  ANTI_DETECTION_CATALOG,
  describeBadge,
  type AntiDetectionTelemetryResponse,
  type AntiDetectionCatalogEntry,
  type AntiDetectionTelemetryRow,
  type SignalOutcome,
} from './catalog';
import { ANTI_DETECTION_SIGNAL_CATALOG } from '../../shared/anti-detection/signals';
import {
  buildStackedSeriesForOutcome,
  summariseRowsBySignal,
} from './aggregate';
import { Sparkline } from './Sparkline';
import { StackedAreaChart } from './StackedAreaChart';
import { GateRejectConfidence } from './GateRejectConfidence';

const { Title, Text, Paragraph } = Typography;

type WindowOption = '1h' | '6h' | '24h' | '7d';

const WINDOW_OPTIONS: { value: WindowOption; label: string; hours: number }[] =
  [
    { value: '1h', label: '1 小時', hours: 1 },
    { value: '6h', label: '6 小時', hours: 6 },
    { value: '24h', label: '24 小時', hours: 24 },
    { value: '7d', label: '7 天', hours: 168 },
  ];

const BUCKET_WIDTH_MS = 60 * 60 * 1000; // 1 hour buckets per spec §3.2

interface FetchResult {
  loading: boolean;
  error: string | null;
  data: AntiDetectionTelemetryResponse | null;
  fetchedAt: string | null;
}

function useTelemetry(
  windowHours: number,
  nodeId?: number
): FetchResult & { refetch: () => Promise<void> } {
  const [state, setState] = useState<FetchResult>({
    loading: true,
    error: null,
    data: null,
    fetchedAt: null,
  });

  const fetchOnce = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = new URLSearchParams({ window: String(windowHours) });
      if (nodeId && nodeId > 0) params.set('nodeId', String(nodeId));
      const res = await authFetch(
        `/api/admin/anti-detection-telemetry?${params.toString()}`
      );
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setState({
          loading: false,
          error: null,
          data: result.data as AntiDetectionTelemetryResponse,
          fetchedAt: new Date().toISOString(),
        });
      } else {
        setState({
          loading: false,
          error: result.msg || '查詢失敗',
          data: null,
          fetchedAt: null,
        });
      }
    } catch (e) {
      setState({
        loading: false,
        error: e instanceof Error ? e.message : '查詢失敗',
        data: null,
        fetchedAt: null,
      });
    }
  }, [windowHours, nodeId]);

  useEffect(() => {
    fetchOnce();
  }, [fetchOnce]);

  return { ...state, refetch: fetchOnce };
}

function parseWindowParam(raw: string | null): WindowOption {
  if (raw === '1h' || raw === '6h' || raw === '24h' || raw === '7d') {
    return raw;
  }
  return '24h';
}

function parseNodeIdParam(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.floor(n);
}

/**
 * Sprint 24 T1-E — Anti-detection telemetry 16-tile dashboard.
 *
 * Renders a grid of 16 anti-detection signal tiles, one per catalog
 * key. Each tile shows the verifyStatus badge (GO / CONDITIONAL /
 * DORMANT) per spec §3.2, a 24-bucket sparkline, and fire / refuse
 * / degrade counters. Click-through goes to the per-signal drill-
 * down route — three stacked-area charts (one per outcome) split by
 * source-table.
 *
 * The data layer is the unified BO endpoint `/api/admin/anti-
 * detection-telemetry` shipped by S23-G; this page never reads any
 * source table directly. Brand scope flows through `authFetch` via
 * the existing `X-Brand` header pattern.
 *
 * Out-of-scope on Sprint 24 (per spec §4.3):
 *   - cross-correlation rotation overlay (Sprint 25)
 *   - adding new signal keys (must update S23-D registry first)
 *   - collapsing audit_log / connectivity_event into one bucket
 */
export const AntiDetectionTelemetryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const windowOption = parseWindowParam(searchParams.get('window'));
  const nodeIdFilter = parseNodeIdParam(searchParams.get('nodeId'));
  const nodeIdRaw = searchParams.get('nodeId') ?? '';
  const windowEntry =
    WINDOW_OPTIONS.find((o) => o.value === windowOption) ?? WINDOW_OPTIONS[2];

  const { loading, error, data, fetchedAt, refetch } = useTelemetry(
    windowEntry.hours,
    nodeIdFilter
  );

  const windowFromMs = useMemo(() => {
    if (data?.from) {
      const t = Date.parse(data.from);
      if (Number.isFinite(t)) return t;
    }
    return Date.now() - windowEntry.hours * BUCKET_WIDTH_MS;
  }, [data?.from, windowEntry.hours]);

  const bucketCount = useMemo(() => {
    if (data?.from && data?.to) {
      const span = Date.parse(data.to) - Date.parse(data.from);
      if (Number.isFinite(span) && span > 0) {
        return Math.max(1, Math.round(span / BUCKET_WIDTH_MS));
      }
    }
    return windowEntry.hours;
  }, [data?.from, data?.to, windowEntry.hours]);

  const summaries = useMemo(
    () =>
      summariseRowsBySignal(
        data?.rows ?? [],
        bucketCount,
        windowFromMs,
        BUCKET_WIDTH_MS
      ),
    [data?.rows, bucketCount, windowFromMs]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space align="center" wrap>
          <RadarChartOutlined style={{ fontSize: 22 }} />
          <Title level={3} style={{ margin: 0 }}>
            反偵測信號儀表板
          </Title>
          <Tag color="blue">Sprint 23 R5</Tag>
          <Tag color="purple">S23-G BO endpoint</Tag>
        </Space>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          16 個 anti-detection 信號的統一面板，取代過往每次客戶 log forensic
          要對 4 個來源表分別 grep 的流程。徽章來自 S23-D 註冊表
          <Text code>verifyStatus</Text>，sparkline 為 1 小時桶聚合。
        </Paragraph>

        <Card size="small" data-testid="anti-detection-filters">
          <Space size={16} wrap>
            <Space size={8}>
              <Text strong>時間視窗</Text>
              <Radio.Group
                value={windowOption}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('window', e.target.value as WindowOption);
                  setSearchParams(next, { replace: false });
                }}
                buttonStyle="solid"
                size="small"
              >
                {WINDOW_OPTIONS.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Space>
            <Space size={8}>
              <Text strong>節點 ID</Text>
              <Input
                aria-label="節點 ID"
                placeholder="留空 = 全節點"
                defaultValue={nodeIdRaw}
                style={{ width: 140 }}
                size="small"
                onBlur={(e) => {
                  const next = new URLSearchParams(searchParams);
                  const value = e.target.value.trim();
                  if (value) {
                    next.set('nodeId', value);
                  } else {
                    next.delete('nodeId');
                  }
                  setSearchParams(next, { replace: false });
                }}
                onPressEnter={(e) => {
                  (e.target as HTMLInputElement).blur();
                }}
              />
            </Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                refetch().catch(() => {
                  message.error('重新整理失敗');
                });
              }}
              size="small"
            >
              重新整理
            </Button>
            {fetchedAt && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                最後更新 {new Date(fetchedAt).toLocaleTimeString()}
              </Text>
            )}
            {data && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Brand <Tag color="default">{data.brand || '全品牌'}</Tag>
                共 <strong>{data.rowCount}</strong> 列
              </Text>
            )}
          </Space>
        </Card>

        {error && (
          <Alert
            type="error"
            message="查詢失敗"
            description={error}
            showIcon
            closable
          />
        )}

        {loading && !data ? (
          <Row gutter={[12, 12]} data-testid="loading-grid">
            {Array.from({ length: 16 }).map((_, idx) => (
              <Col xs={24} sm={12} md={8} lg={6} key={idx}>
                <Card size="small">
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <Row gutter={[12, 12]} data-testid="anti-detection-grid">
            {ANTI_DETECTION_CATALOG.map((entry) => (
              <Col xs={24} sm={12} md={8} lg={6} key={entry.key}>
                <SignalTile
                  entry={entry}
                  summary={summaries.get(entry.key)}
                  windowOption={windowOption}
                  nodeIdRaw={nodeIdRaw}
                />
              </Col>
            ))}
          </Row>
        )}

        <GateRejectConfidence />
      </Space>
    </div>
  );
};

interface SignalTileProps {
  entry: AntiDetectionCatalogEntry;
  summary?: ReturnType<typeof summariseRowsBySignal> extends Map<
    string,
    infer V
  >
    ? V
    : never;
  windowOption: WindowOption;
  nodeIdRaw: string;
}

const SignalTile: React.FC<SignalTileProps> = ({
  entry,
  summary,
  windowOption,
  nodeIdRaw,
}) => {
  const badge = describeBadge(entry.verifyStatus);
  const fire = summary?.counts.fire ?? 0;
  const refuse = summary?.counts.refuse ?? 0;
  const degrade = summary?.counts.degrade ?? 0;
  const total = summary?.totalCount ?? 0;
  const lastFiredAt = summary?.lastFiredAt ?? '';
  const sparklineValues = summary?.sparklineValues ?? [];

  const params = new URLSearchParams({ window: windowOption });
  if (nodeIdRaw) params.set('nodeId', nodeIdRaw);
  const drillHref = `/admin/anti-detection-telemetry/${encodeURIComponent(
    entry.key
  )}?${params.toString()}`;

  return (
    <Card
      size="small"
      data-testid={`signal-tile-${entry.key}`}
      hoverable
      bodyStyle={{ padding: 12 }}
      style={{ height: '100%' }}
    >
      <Link
        to={drillHref}
        aria-label={`${entry.label} 詳情 (${badge.label})`}
        style={{ color: 'inherit', display: 'block' }}
      >
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Space size={4} wrap>
            <Tooltip title={badge.tooltip}>
              <Tag color={badge.color} data-testid={`badge-${entry.key}`}>
                {badge.label}
              </Tag>
            </Tooltip>
            {entry.prohibitScopes?.length ? (
              <Tooltip
                title={`PROHIBIT scopes: ${entry.prohibitScopes.join(', ')}`}
              >
                <Tag color="red">PROHIBIT</Tag>
              </Tooltip>
            ) : null}
            <Text strong style={{ fontSize: 13 }}>
              {entry.label}
            </Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <code style={{ fontSize: 10 }}>{entry.key}</code>
          </Text>
          <Sparkline
            values={sparklineValues}
            ariaLabel={`${entry.label} ${windowOption} sparkline`}
            color={badge.color === 'green' ? '#52c41a' : '#1677ff'}
          />
          <Row gutter={4}>
            <Col span={8}>
              <Statistic
                title="fire"
                value={fire}
                valueStyle={{ fontSize: 14, color: '#52c41a' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="refuse"
                value={refuse}
                valueStyle={{ fontSize: 14, color: '#ff4d4f' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="degrade"
                value={degrade}
                valueStyle={{ fontSize: 14, color: '#faad14' }}
              />
            </Col>
          </Row>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {total === 0
              ? '視窗內無資料'
              : lastFiredAt
              ? `最後事件 ${new Date(lastFiredAt).toLocaleString()}`
              : `合計 ${total}`}
          </Text>
        </Space>
      </Link>
    </Card>
  );
};

/**
 * Per-signal drill-down route. Three stacked-area charts split per
 * source-table per spec §3.3. Window state is mirrored through URL
 * query so refresh / deep-link survives.
 */
export const AntiDetectionSignalDrillDown: React.FC = () => {
  const { signalKey } = useParams<{ signalKey: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const windowOption = parseWindowParam(searchParams.get('window'));
  const nodeIdFilter = parseNodeIdParam(searchParams.get('nodeId'));
  const windowEntry =
    WINDOW_OPTIONS.find((o) => o.value === windowOption) ?? WINDOW_OPTIONS[2];

  const catalog = ANTI_DETECTION_CATALOG.find((e) => e.key === signalKey);
  const { loading, error, data, refetch } = useTelemetry(
    windowEntry.hours,
    nodeIdFilter
  );

  const windowFromMs = useMemo(() => {
    if (data?.from) {
      const t = Date.parse(data.from);
      if (Number.isFinite(t)) return t;
    }
    return Date.now() - windowEntry.hours * BUCKET_WIDTH_MS;
  }, [data?.from, windowEntry.hours]);

  const bucketCount = useMemo(() => {
    if (data?.from && data?.to) {
      const span = Date.parse(data.to) - Date.parse(data.from);
      if (Number.isFinite(span) && span > 0) {
        return Math.max(1, Math.round(span / BUCKET_WIDTH_MS));
      }
    }
    return windowEntry.hours;
  }, [data?.from, data?.to, windowEntry.hours]);

  if (!signalKey || !catalog) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="未知的信號鍵" />
        <Button
          type="link"
          onClick={() => navigate('/admin/anti-detection-telemetry')}
        >
          返回 16-tile 儀表板
        </Button>
      </div>
    );
  }

  const badge = describeBadge(catalog.verifyStatus);
  const rows = data?.rows ?? [];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space align="center" wrap>
          <Link to="/admin/anti-detection-telemetry">
            <Button size="small">{'← 返回 16-tile'}</Button>
          </Link>
          <Title level={3} style={{ margin: 0 }}>
            {catalog.label}
          </Title>
          <Tag color={badge.color}>{badge.label}</Tag>
          {catalog.prohibitScopes?.length ? <Tag color="red">PROHIBIT</Tag> : null}
          <Text code>{catalog.key}</Text>
        </Space>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {catalog.description}
        </Paragraph>

        <Card size="small">
          <Space size={16} wrap>
            <Space size={8}>
              <Text strong>時間視窗</Text>
              <Radio.Group
                value={windowOption}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  next.set('window', e.target.value as WindowOption);
                  setSearchParams(next, { replace: false });
                }}
                buttonStyle="solid"
                size="small"
              >
                {WINDOW_OPTIONS.map((opt) => (
                  <Radio.Button key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                refetch().catch(() => message.error('重新整理失敗'));
              }}
              size="small"
            >
              重新整理
            </Button>
          </Space>
        </Card>

        {error && (
          <Alert type="error" message="查詢失敗" description={error} showIcon />
        )}

        {loading && !data ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : rows.length === 0 ? (
          <Empty description="視窗內沒有任何來源表寫入此信號" />
        ) : (
          <Row gutter={[12, 12]}>
            {(['fire', 'refuse', 'degrade'] as SignalOutcome[]).map(
              (outcome) => (
                <Col span={24} key={outcome}>
                  <OutcomeChart
                    outcome={outcome}
                    rows={rows}
                    signalKey={catalog.key}
                    bucketCount={bucketCount}
                    windowFromMs={windowFromMs}
                  />
                </Col>
              )
            )}
          </Row>
        )}
      </Space>
    </div>
  );
};

/**
 * Sprint 25 / S25-M — minimal landing for the anti-detection telemetry
 * dashboard route. T1-E lands the full 16-tile observability dashboard
 * (AntiDetectionTelemetryPage above); this Index page is the parent the
 * S25-M correlation page links back to and the spot where the
 * "View Correlation Matrix" CTA lives.
 *
 * Both pages are exported so callers can use either:
 *   - `AntiDetectionTelemetryPage`  — full 16-tile observability grid (T1-E)
 *   - `AntiDetectionTelemetryIndex` — landing page with correlation CTA (S25-M)
 */
export const AntiDetectionTelemetryIndex: React.FC = () => {
  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>
        <ExperimentOutlined /> Anti-detection telemetry
      </Title>
      <Paragraph type="secondary">
        16 個 anti-detection signal 的監控總覽。每個 tile 對應一個
        canonical signal；點下面的 CTA 進入 Sprint 25 W2 的
        cross-correlation matrix 看 signal 之間的關聯。
      </Paragraph>

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space>
            <Text strong>16-tile dashboard</Text>
            <Tag color="default">T1-E baseline</Tag>
          </Space>
          <Text type="secondary">
            分桶 A/B/C/D 各 4 tile：refusal / fire-marker probe / connectivity
            outcome / decoy。下面是當前 catalog；live counter tiles 由 T1-E
            主分支提供。
          </Text>
        </Space>
      </Card>

      <Card
        title={
          <Space>
            <HeatMapOutlined />
            <span>Sprint 25 W2 · cross-correlation matrix</span>
          </Space>
        }
        style={{ marginBottom: 16 }}
        extra={
          <Link to="/anti-detection-telemetry/correlation">
            <Space>
              <span>View Correlation Matrix</span>
              <ArrowRightOutlined />
            </Space>
          </Link>
        }
      >
        <Paragraph>
          16×16 Pearson product-moment 相關矩陣。把 24h 內每個 signal 的
          hour-bucket fire-rate 兩兩做相關性分析，找出例如「invalid-ticket
          refusal 飆升時 handshake-RST fire-rate 反而下降」這種隱性關聯。
        </Paragraph>
      </Card>

      <Card title="Signal catalog (16 keys, 4 buckets)">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {ANTI_DETECTION_SIGNAL_CATALOG.map((sig, i) => (
            <Card
              key={sig.key}
              size="small"
              data-testid={`tile-${sig.key}`}
              title={
                <Space>
                  <Tag color={bucketColor(sig.bucket)}>
                    {sig.bucket}
                    {i + 1}
                  </Tag>
                  <Text strong>{sig.label}</Text>
                </Space>
              }
            >
              <Text type="secondary" style={{ fontSize: 11 }}>
                source: <code>{sig.source}</code>
              </Text>
            </Card>
          ))}
        </div>
      </Card>
    </div>
  );
};

interface OutcomeChartProps {
  outcome: SignalOutcome;
  rows: AntiDetectionTelemetryRow[];
  signalKey: string;
  bucketCount: number;
  windowFromMs: number;
}

const OUTCOME_LABELS: Record<SignalOutcome, string> = {
  fire: '點火 (fire)',
  refuse: '拒絕 (refuse)',
  degrade: '降級 (degrade)',
};

const OutcomeChart: React.FC<OutcomeChartProps> = ({
  outcome,
  rows,
  signalKey,
  bucketCount,
  windowFromMs,
}) => {
  const { series, bucketLabels } = useMemo(
    () =>
      buildStackedSeriesForOutcome(
        rows,
        signalKey,
        outcome,
        bucketCount,
        windowFromMs,
        BUCKET_WIDTH_MS
      ),
    [rows, signalKey, outcome, bucketCount, windowFromMs]
  );
  const totalCount = series.reduce(
    (acc, s) => acc + s.values.reduce((a, b) => a + b, 0),
    0
  );
  return (
    <Card
      size="small"
      title={
        <Space>
          <Text strong>{OUTCOME_LABELS[outcome]}</Text>
          <Tag>{totalCount} 筆</Tag>
        </Space>
      }
      data-testid={`drill-down-outcome-${outcome}`}
    >
      <StackedAreaChart
        series={series}
        bucketLabels={bucketLabels}
        ariaLabel={`${signalKey} ${outcome} 來源拆分`}
      />
    </Card>
  );
};

function bucketColor(bucket: 'A' | 'B' | 'C' | 'D'): string {
  switch (bucket) {
    case 'A':
      return 'volcano';
    case 'B':
      return 'orange';
    case 'C':
      return 'gold';
    case 'D':
      return 'magenta';
  }
}

export default AntiDetectionTelemetryIndex;
